/**
 * Habitaciones del inmueble.
 *
 * La sección YA EXISTÍA al final de la ficha, en modo solo lectura; lo que no había era manera
 * de tocarlas. Se conserva su sitio, su disposición en rejilla y su subtítulo con la ocupación,
 * y se le añaden las tres acciones que faltaban: añadir, editar y borrar.
 *
 * Importa que estén AQUÍ y no escondidas en el panel de edición del inmueble, porque la propia
 * aplicación insiste —con razón— en que LA OCUPACIÓN Y LOS INDICADORES SALEN DE LAS HABITACIONES,
 * no de los dormitorios: un inmueble con 4 dormitorios y 0 habitaciones aparece al 0 % de
 * ocupación, y el alta hasta promete que las habitaciones «luego se editan una a una».
 *
 * EL ESTADO NO SE EDITA A MANO, y por eso `updateUnitSchema` lo excluye en el servidor: una
 * habitación pasa a ocupada porque hay un inquilino asignado, no porque alguien lo escriba. Y se
 * asigna desde la ficha del inquilino, no desde aquí: primero llega la persona y luego se le
 * coloca. Esta pantalla enseña el resultado.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Money, UnitDto } from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Icon } from '../../components/Icon';
import { ConfirmDialog } from '../../components/overlay/Drawer';
import { toast } from '../../components/feedback/Toaster';
import { formatEur, formatNumber, formatPercent } from '../../lib/format';
import { UnitFormDrawer } from './UnitFormDrawer';
import styles from './PropertyUnits.module.css';

interface PropertyUnitsProps {
  propertyId: string;
  units: UnitDto[];
  occupancyRate: number;
  monthlyRent: Money;
  canManage: boolean;
}

export function PropertyUnits({
  propertyId,
  units,
  occupancyRate,
  monthlyRent,
  canManage,
}: PropertyUnitsProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<UnitDto | 'nueva' | null>(null);
  const [deleting, setDeleting] = useState<UnitDto | null>(null);

  /**
   * Devuelve la promesa del refresco A PROPOSITO: quien borra la espera antes de cerrar su
   * dialogo. Sin eso, el dialogo se va, la lista sigue un instante con la fila que acabas de
   * borrar y el usuario ve —brevemente— algo que ya no existe.
   */
  function refresh(): Promise<unknown> {
    // La ocupación del listado y del panel se calcula con las habitaciones: tocarlas mueve la cifra.
    void queryClient.invalidateQueries({ queryKey: ['properties'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    return queryClient.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
  }

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteUnit(id),
    onSuccess: async () => {
      await refresh();
      toast.success('Habitación borrada.');
      setDeleting(null);
    },
    onError: (error: unknown) => {
      // El 409 de contrato activo llega con el código de la habitación dentro del mensaje.
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido borrar.');
      setDeleting(null);
    },
  });

  return (
    <>
      <Card variant="table" className={styles.card}>
        <CardHeader
          title="Habitaciones"
          subtitle={`${units.length} habitaciones · ocupación ${formatPercent(
            occupancyRate,
            0,
          )} · renta contratada ${formatEur(monthlyRent)}/mes`}
          {...(canManage
            ? {
                aside: (
                  <Button variant="secondary" size="sm" onClick={() => setEditing('nueva')}>
                    <Icon name="plus" size={14} strokeWidth={2.4} />
                    Añadir habitación
                  </Button>
                ),
              }
            : {})}
        />

        {units.length === 0 ? (
          <p className={styles.empty}>
            Este inmueble no tiene habitaciones, así que aparece al 0 % de ocupación y no se puede
            alquilar. Añade una por cada espacio que se alquile por separado.
          </p>
        ) : (
          units.map((unit) => (
            <div
              key={unit.id}
              className={canManage ? `${styles.row} ${styles.rowManaged}` : styles.row}
            >
              <span className={styles.code}>{unit.code}</span>
              <span className={styles.tenant}>
                {unit.currentLease ? (
                  unit.currentLease.tenants.map((tenant, index) => (
                    <span key={tenant.id}>
                      {index > 0 && ', '}
                      {/* El nombre lleva a su ficha: es donde se renueva o se rescinde. */}
                      <Link to={`/inquilinos/${tenant.id}`} className={styles.tenantLink}>
                        {tenant.fullName}
                      </Link>
                    </span>
                  ))
                ) : (
                  /*
                   * Antes aquí había un «Alquilar» que llevaba al asistente de contrato. Ahora
                   * la habitación se asigna DESDE EL INQUILINO —que es como se trabaja: primero
                   * llega la persona, luego se le coloca—, así que aquí solo se informa.
                   */
                  'Libre'
                )}
              </span>
              <span className={styles.rent}>
                {formatEur(unit.currentLease?.monthlyRent ?? unit.baseRent)}
              </span>
              <span className={styles.area}>
                {unit.areaM2 ? `${formatNumber(unit.areaM2)} m²` : '—'}{' '}
                {unit.status === 'ocupada' ? (
                  <Badge tone="success" size="sm">
                    Ocupada
                  </Badge>
                ) : (
                  <Badge tone="neutral" size="sm">
                    {unit.status === 'libre' ? 'Libre' : 'No disponible'}
                  </Badge>
                )}
              </span>

              {canManage && (
                <span className={styles.actions}>
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => setEditing(unit)}
                    aria-label={`Editar ${unit.code}`}
                  >
                    <Icon name="edit" size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                    onClick={() => setDeleting(unit)}
                    /**
                     * Con contrato vigente el servidor devuelve 409. Se deshabilita aquí para no
                     * ofrecer una acción que va a fallar, pero la regla vive en el servidor:
                     * esto solo evita el viaje.
                     */
                    disabled={unit.currentLease !== null}
                    title={
                      unit.currentLease
                        ? 'Tiene un contrato vigente. Recíndelo antes de borrar la habitación.'
                        : `Borrar ${unit.code}`
                    }
                    aria-label={`Borrar ${unit.code}`}
                  >
                    <Icon name="trash" size={14} strokeWidth={2} />
                  </button>
                </span>
              )}
            </div>
          ))
        )}
      </Card>

      <UnitFormDrawer
        open={editing !== null}
        propertyId={propertyId}
        {...(editing && editing !== 'nueva' ? { unit: editing } : {})}
        onClose={() => setEditing(null)}
        onSaved={refresh}
      />

      {deleting && (
        <ConfirmDialog
          title={`Borrar ${deleting.code}`}
          body="La habitación deja de contar para la ocupación y ya no se puede asignar a nadie. El histórico de cargos y pagos se conserva."
          confirmLabel="Borrar"
          tone="danger"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await remove.mutateAsync(deleting.id);
          }}
        />
      )}
    </>
  );
}
